import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, User } from 'lucide-react';
import SEO from '../components/SEO';
import { Card } from '../components/ui/card';
import { blogPosts } from '../data/blog';

const Blog = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Blog | Web Growth Studio - Web Design & Digital Marketing Tips"
        description="Expert insights on web design, SEO, and digital marketing strategies to help your business grow online. Read our latest articles and tips."
        keywords="web design blog, SEO tips, digital marketing advice, website optimization, business growth"
        url="https://webgrowthstudio.com/blog"
      />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
            Our <span className="text-cyan-500">Blog</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Insights, tips, and strategies to help your business thrive online
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <Card key={post.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 rounded-lg group">
                <Link to={`/blog/${post.slug}`}>
                  <div className="h-48 bg-gray-100 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(post.date).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {post.readTime}
                      </span>
                    </div>
                    <span className="inline-block px-3 py-1 bg-cyan-100 text-cyan-700 text-xs font-medium rounded-full mb-3">
                      {post.category}
                    </span>
                    <h2 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-cyan-500 transition-colors line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <span className="inline-flex items-center text-cyan-500 font-medium text-sm group-hover:gap-2 transition-all">
                      Read More
                      <ArrowRight className="ml-1 w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </Card>
            ))}
          </div>

          {blogPosts.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-600 text-lg">No blog posts yet. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Want a Website That Converts?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Let's discuss how we can help your business grow online.
          </p>
          <Link to="/contact">
            <button className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-full px-8 py-4 text-lg font-medium transition-colors">
              Get Your Free Consultation
              <ArrowRight className="ml-2 w-5 h-5 inline" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Blog;
